import { Inject, Injectable, Optional } from '@angular/core';
import { defer, forkJoin, from, mergeMap, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { ConcurrentStorageData } from '../../types/concurrent-usage';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { safeStringify } from '@evorait/pace-components';
import { ConcurrentUsageDbService } from './concurrent-usage-db.service';
import { UserStoreApiService } from '../user-store/user-store-api.service';
import { UserStoreEntry } from '../../state/user-store/user-store.wire';
import { USER_STORE_SYNCED_KEYS } from '../../state/user-store/user-store.state';
import {
    PERSISTED_SLICES,
    PersistedSlice,
    persistedSliceStorageKey,
} from '../../state/persistence/persistence.registry';
import { isDefined } from '../../utilities/is-defined';
import { DEPRECATED_MIGRATION_CONCURRENT_USAGE_KEYS, LEGACY_CONCURRENT_USAGE_KEYS } from './concurrent-usage-keys';

/**
 * `AzUserStore` is a single flat per-user key-value namespace shared with other domains (e.g.
 * `location-tracker`, see `LOCATION_TRACKER_STORE_KEY`). There is no automatic namespacing here —
 * every caller (this service's own individual-key methods, `UserStoreEffects`, etc.) is
 * responsible for picking a key that does not collide with anyone else's. Each domain therefore
 * reads and writes only the keys it owns: the device handover (`downloadAll`/`uploadAll`) moves
 * the keys in `handoverKeys`, and the user-store slice its own `USER_STORE_SYNCED_KEYS`. Every
 * value this service uploads is also wrapped as `{ value, timestamp }` (see `writeRemote`/
 * `ConcurrentUsageRemoteData`) — `isConcurrentUsageRemoteData` still checks that shape on the way
 * back, so an entry some other writer stored under one of these keys is ignored instead of misread.
 */

/** The `data` payload `ConcurrentUsageService` stores under each user-store entry. */
interface ConcurrentUsageRemoteData {
    value: string;
    timestamp: number;
}

/**
 * Marks a binary entry as base64-encoded (see `writeBinaryDataForKey`). An entry written before
 * that encoding was introduced carries no marker and is read back as latin1 instead.
 */
const BINARY_BASE64_PREFIX = 'b64:';

/** Bytes per `String.fromCharCode` call while base64-encoding — see `bytesToMarkedBase64`. */
const BASE64_CHUNK_SIZE = 8192;

@Injectable({ providedIn: 'root' })
export class ConcurrentUsageService {
    /**
     * The keys of the device handover — what `downloadAll` restores on check-in: every persisted
     * slice that is not `localOnly` (see `PERSISTED_SLICES`), the storage services without a slice
     * yet (`LEGACY_CONCURRENT_USAGE_KEYS`), and, for the migration period only, the legacy keys the
     * migration still reads (`DEPRECATED_MIGRATION_CONCURRENT_USAGE_KEYS`).
     */
    private readonly handoverKeys: readonly string[];
    /**
     * What `uploadAll` sends on check-out: the handover keys plus `USER_STORE_SYNCED_KEYS`. The
     * user-store slice uploads its values immediately on every change, but that upload can fail;
     * the local copy `writeDataForKey` kept is sent again here, so nothing is lost when the user
     * switches devices. The user-store keys are NOT downloaded — the slice loads them itself.
     */
    private readonly uploadKeys: ReadonlySet<string>;

    public constructor(
        private readonly userStoreApi: UserStoreApiService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly paceInsights: PaceInsightsService,
        private readonly concurrentUsageDbService: ConcurrentUsageDbService,
        @Optional() @Inject(PERSISTED_SLICES) persistedSlices: PersistedSlice[] | null = null,
    ) {
        const sliceKeys = (persistedSlices ?? [])
            .filter((slice) => !slice.localOnly)
            .map((slice) => persistedSliceStorageKey(slice.featureKey));
        this.handoverKeys = [
            ...new Set([
                ...sliceKeys,
                ...Object.values(LEGACY_CONCURRENT_USAGE_KEYS),
                ...DEPRECATED_MIGRATION_CONCURRENT_USAGE_KEYS,
            ]),
        ];
        this.uploadKeys = new Set([...this.handoverKeys, ...USER_STORE_SYNCED_KEYS]);
    }

    /**
     * Restores the device handover on check-in: requests just the `handoverKeys` in one bulk read
     * and replaces their local rows with the result (a key the backend has nothing for is removed
     * locally). Local rows of other keys — e.g. a `localOnly` persisted slice — stay untouched.
     *
     * The local copies of `USER_STORE_SYNCED_KEYS` are dropped as well, without being downloaded:
     * `uploadAll` sends whatever it finds under those keys, and a copy left over from before
     * another device changed the value would otherwise overwrite that newer value on the next
     * check-out. The user-store slice reads its current values from Azure itself.
     */
    public downloadAll(): Observable<unknown> {
        // defer ensures a fresh start timestamp for every subscription (important if method is reused).
        return defer(() => {
            const start = performance.now(); // high-resolution timestamp
            return this.userStoreApi.getEntries(this.handoverKeys).pipe(
                map((entries) => {
                    this.log.debug(`ConcurrentUsageService: downloadAll: SUCCESS loading all data`);
                    return entries
                        .filter((entry) => ConcurrentUsageService.isConcurrentUsageRemoteData(entry.data))
                        .map((entry) =>
                            ConcurrentUsageService.toConcurrentStorageData(
                                entry.id,
                                entry.data as ConcurrentUsageRemoteData,
                            ),
                        );
                }),
                mergeMap(async (results: ConcurrentStorageData[]) => {
                    const data = results.reduce(
                        (acc, curr) => {
                            acc[curr.key] = curr.value;
                            return acc;
                        },
                        {} as { [key: string]: string },
                    );
                    this.paceInsights.trackEvent('ConcurrentUsageService::downloadAll::result', {
                        ...data,
                        durationMs: Math.round(performance.now() - start),
                    });
                    await this.concurrentUsageDbService.replaceKeys(
                        [...this.handoverKeys, ...USER_STORE_SYNCED_KEYS],
                        results,
                    );
                }),
            );
        });
    }

    /**
     * Uploads everything the local store still owes the backend: the present entries of the
     * `uploadKeys` and the deletions. The present entries go out in one bulk write (see
     * `writeRemoteAll`); a local entry of any other key — e.g. a `localOnly` persisted slice — is
     * never uploaded. Both halves run to the end independently of each other (see
     * `uploadIndependently`), so a failing delete cannot stop a pending write from being uploaded,
     * or the other way round.
     *
     * Completes without emitting if a half does — `DeviceLockLostInterceptor` swallows a 423 with
     * `EMPTY`, which the caller has to tell apart from a real upload (see
     * `DeviceLockManagementModalComponent.releaseLock`'s `defaultIfEmpty`).
     *
     * `onlineOnly` sends the writes straight to the network instead of through the offline request
     * queue (see `UserStoreApiService.putEntry`), so this observable reports what the backend
     * really did rather than what was queued. The device-lock check-out needs that — it may only
     * release the lock once the data has genuinely landed — and can afford it, because check-out
     * is impossible while offline. Nothing is risked by a failure either: `writeDataForKey` has
     * already persisted every entry locally, so a failed direct write stays in the local store and
     * the next `uploadAll` sends it again. The deletions are unaffected, they bypass the queue
     * either way (see `UserStoreApiService.deleteEntry`).
     */
    public uploadAll(onlineOnly = false): Observable<unknown> {
        return this.uploadIndependently(
            [
                { name: 'presentEntries', upload$: this.uploadPresentEntries(onlineOnly) },
                { name: 'deletedEntries', upload$: this.uploadDeletedEntries() },
            ],
            'uploadAll',
        );
    }

    /**
     * Writes `value` into the local encrypted store under `key`. Deferred (batch) upload is the
     * default — `uploadAll` picks this entry up next time it runs — but a caller that cannot wait
     * for that (e.g. `UserStoreEffects`, which write-throughs to Azure on every change) can pass
     * `uploadImmediately: true` to also send it to Azure right away, via `writeRemote`, as part of
     * this same call. The local write always happens first and is never rolled back if that upload
     * fails — the cache reflects what the caller asked to store regardless of network state; a
     * caller wanting to react to the upload outcome subscribes to the returned observable.
     *
     * Both halves are lazy: nothing is written until the returned observable is subscribed to (the
     * local write is wrapped in `defer` for exactly that reason). Without it the local `put` would
     * start at call time while the upload waited for a subscription, so a caller that never
     * subscribed would store locally but silently never upload, and a caller subscribing twice
     * would upload twice.
     */
    public writeDataForKey(
        key: string,
        value: string,
        timestamp: number,
        uploadImmediately = false,
    ): Observable<ConcurrentStorageData> {
        const concurrencyData: ConcurrentStorageData = { key, value, timestamp };
        this.log.debug(`ConcurrentUsageService: writeDataForKey: writing data for key "${key}"`);
        this.paceInsights.trackEvent(`ConcurrentUsageService:writeDataForKey:${key}`, {
            concurrencyData,
        });

        // store locally; deferred so that neither half runs before the caller subscribes
        const write$ = defer(() => from(this.concurrentUsageDbService.put(concurrencyData))).pipe(
            map(() => concurrencyData),
        );
        return uploadImmediately
            ? write$.pipe(switchMap((data) => this.writeRemote(data).pipe(map(() => data))))
            : write$;
    }

    public readDataForKey(key: string): Observable<ConcurrentStorageData[]> {
        this.log.debug(`ConcurrentUsageService: readDataForKey: reading data for key "${key}"`);

        return from(this.concurrentUsageDbService.get(key)).pipe(
            take(1),
            map((data) => {
                if (!data?.value) {
                    return [];
                }
                return [data];
            }),
        );
    }

    /**
     * Binary-payload variant of `writeDataForKey`. Encodes the bytes as base64 so they travel
     * through the same encrypt-and-persist (and remote-sync) path as string values.
     *
     * Why binary payloads are carried as a string rather than stored as raw bytes:
     *
     * This store is not local-only — it is mirrored to the backend. `uploadAll` reads every
     * present entry, `writeRemote` sends each `{ value, timestamp }` payload as the user-store
     * entry's `data`, and `downloadAll` -> `ConcurrentUsageDbService.replaceKeys` DROPS the local
     * rows of the handover keys and rebuilds them from the remote results. So an entry only survives a download if it
     * can round-trip through that string/JSON transport; a raw `Uint8Array` value cannot, and
     * would be wiped on the next `downloadAll` (triggered by the device-lock flow). The whole
     * pipeline — `ConcurrentStorageData.value`, `EncryptionService.encryptAsync`, the user-store
     * entry — is therefore string-typed by design.
     *
     * Why base64 and not the latin1 mapping this used to apply: the remote half of that pipeline
     * is JSON, and the payload is gzip output, i.e. uniformly distributed bytes. As latin1, ~1/8
     * of them land in 0x00-0x1F and `JSON.stringify` expands each of those to a six-character
     * `\u00XX` escape, while every byte above 0x7F costs two bytes in utf-8 — together roughly
     * 2.5-3x the raw payload in the stored document. Base64 is a flat 1.33x and needs no JSON
     * escaping at all, which is what keeps an entry clear of the backend's per-item size limit
     * (see `UserStoreDatabaseService`, which offloads oversized values to blob storage).
     *
     * Entries written before this change are still latin1, so the encoding is marked: a value
     * written here carries {@link BINARY_BASE64_PREFIX}, and `readBinaryDataForKey` falls back to
     * the latin1 decoding when the marker is absent. The marker cannot be mistaken for legacy
     * content — a latin1 payload is gzip output and always starts with the magic bytes 0x1F 0x8B.
     *
     * If the service is ever refactored to send binary data to the backend (binary user-store
     * payload + a binary-aware `replaceKeys`), this base64 hop — and the string-typed value — can
     * be revisited in favour of storing/transporting raw bytes.
     *
     * @param key the storage key
     * @param value the binary payload to store
     * @param timestamp the write timestamp
     * @returns the stored concurrency data
     */
    public writeBinaryDataForKey(
        key: string,
        value: ArrayBuffer,
        timestamp: number,
    ): Observable<ConcurrentStorageData> {
        return this.writeDataForKey(key, ConcurrentUsageService.bytesToMarkedBase64(value), timestamp);
    }

    /**
     * Binary-payload variant of `readDataForKey`. Reverses whichever encoding
     * `writeBinaryDataForKey` applied — base64 for anything written since, latin1 for entries
     * still carrying the legacy encoding; empty when nothing is stored for the key.
     *
     * @param key the storage key
     * @returns the stored bytes, or an empty array when nothing is stored
     */
    public readBinaryDataForKey(key: string): Observable<ArrayBuffer[]> {
        return this.readDataForKey(key).pipe(
            map((entries) => entries.map((entry) => ConcurrentUsageService.markedStringToBytes(entry.value))),
        );
    }

    /**
     * Encodes bytes as base64 behind {@link BINARY_BASE64_PREFIX}; the inverse of
     * `markedStringToBytes`.
     *
     * `btoa` takes a latin1 string, so the bytes are mapped to code units first — in chunks,
     * because spreading a multi-megabyte array into `String.fromCharCode` blows the call stack.
     *
     * @param bytes the bytes to encode
     * @returns the marked base64 string carrying the bytes
     */
    private static bytesToMarkedBase64(bytes: ArrayBuffer): string {
        const view = new Uint8Array(bytes);
        let latin1 = '';
        for (let offset = 0; offset < view.length; offset += BASE64_CHUNK_SIZE) {
            latin1 += String.fromCharCode(...view.subarray(offset, offset + BASE64_CHUNK_SIZE));
        }
        return BINARY_BASE64_PREFIX + btoa(latin1);
    }

    /**
     * Decodes what `bytesToMarkedBase64` wrote, or — for an entry written before the encoding
     * changed, which carries no marker — reads each code unit back to a byte (latin1).
     *
     * @param value the stored string to decode
     * @returns the decoded bytes
     */
    private static markedStringToBytes(value: string): ArrayBuffer {
        const latin1 = value.startsWith(BINARY_BASE64_PREFIX) ? atob(value.slice(BINARY_BASE64_PREFIX.length)) : value;

        const bytes = new Uint8Array(latin1.length);
        for (let index = 0; index < latin1.length; index++) {
            bytes[index] = latin1.charCodeAt(index);
        }
        return bytes.buffer;
    }

    public getAllPresentEntries(): Observable<ConcurrentStorageData[]> {
        this.log.debug('ConcurrentUsageService: getAllPresentEntries: loading all present entries');
        return from(this.concurrentUsageDbService.getPresentEntries());
    }

    public liveQueryDataForKey$(key: string): Observable<ConcurrentStorageData | undefined> {
        return this.concurrentUsageDbService.liveQuery$(key);
    }

    /**
     * Marks `key` deleted in the local store. Deferred (batch) upload is the default — see
     * `writeDataForKey`'s doc comment, the same reasoning applies here via `uploadDeletedEntries`
     * — but `uploadImmediately: true` also sends the delete to Azure right away, via `deleteRemote`.
     *
     * Both halves are lazy, for the same reason as in `writeDataForKey` — see the `defer` note
     * there.
     *
     * With `uploadImmediately`, the local tombstone is dropped again once the remote delete has
     * landed — see `deleteRemoteAndPurge`. Without it the tombstone is dropped by
     * `uploadDeletedEntries` instead, whenever the deferred upload gets its turn.
     */
    public deleteDataForKey(key: string, uploadImmediately = false): Observable<unknown> {
        this.log.debug(`ConcurrentUsageService: deleteDataForKey: deleting key "${key}"`);
        const remove$ = defer(() => from(this.concurrentUsageDbService.remove(key)));
        return uploadImmediately ? remove$.pipe(switchMap(() => this.deleteRemoteAndPurge(key))) : remove$;
    }

    /**
     * Sends the remote delete for `key` and, only once it has landed, drops the local tombstone
     * `deleteDataForKey` left behind.
     *
     * The tombstone exists to remember that the remote delete is still owed, so it must outlive a
     * failed attempt (it is retried by the next `uploadAll`) and must not outlive a successful one:
     * re-sending a DELETE for a key that is already gone answers 404, and `HttpOnlineInterceptor`
     * shows the user an error window for any 4xx — `UserStoreApiService.deleteEntry` mapping that
     * 404 to success comes too late to prevent the window, which is why the 404 is ALSO declared
     * expected there. Purging keeps the request from being sent in the first place; the declaration
     * covers the 404 that is unavoidable, for a key that never reached Azure at all.
     *
     * @param key the key to delete remotely
     * @returns an observable completing once the delete has landed and the tombstone is gone
     */
    private deleteRemoteAndPurge(key: string): Observable<unknown> {
        return this.deleteRemote(key).pipe(switchMap(() => from(this.concurrentUsageDbService.purge(key))));
    }

    private uploadDeletedEntries(): Observable<unknown> {
        this.log.debug('ConcurrentUsageService::uploadDeletedEntries');
        return from(this.concurrentUsageDbService.getDeletedEntries()).pipe(
            switchMap((allKeys: string[]) => {
                this.log.debug(
                    `ConcurrentUsageService: uploadDeletedEntries: uploading deletion of ${allKeys.join(', ')} (${allKeys.length}) entries to remote`,
                );

                // each tombstone is dropped as soon as its own delete lands — see deleteRemoteAndPurge
                return this.uploadIndependently(
                    allKeys.map((key) => ({ name: key, upload$: this.deleteRemoteAndPurge(key) })),
                    'uploadDeletedEntries',
                );
            }),
        );
    }

    private uploadPresentEntries(onlineOnly = false): Observable<unknown> {
        this.log.debug('ConcurrentUsageService::uploadPresentEntries');
        return from(this.concurrentUsageDbService.getPresentEntries()).pipe(
            map((allEntries) => allEntries.filter((entry) => this.uploadKeys.has(entry.key))),
            switchMap((entries: ConcurrentStorageData[]) => {
                this.log.debug(
                    `ConcurrentUsageService: uploadAll: uploading ${entries.length} entries to remote: ${entries.map((entry) => entry.key).join(', ')}`,
                );
                return this.writeRemoteAll(entries, onlineOnly);
            }),
        );
    }

    /**
     * Bulk variant of `writeRemote`: uploads all `entries` in one request (see
     * `UserStoreApiService.putEntries`). The backend still writes every entry on its own, so one
     * rejected key does not keep the others from landing; the request then fails as a whole and
     * the next `uploadAll` sends everything again, which is safe because each write is a full
     * replace.
     *
     * @param entries the entries to upload
     * @param onlineOnly bypass the offline request queue — see `uploadAll`
     * @returns an observable emitting `true` once every entry has been written
     */
    private writeRemoteAll(entries: ConcurrentStorageData[], onlineOnly: boolean): Observable<unknown> {
        if (entries.length === 0) {
            return of(true);
        }

        const keys = entries.map((entry) => entry.key);
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemoteAll', { state: 'attempt', id, keys });
        const start = performance.now(); // high-resolution timestamp

        return this.userStoreApi
            .putEntries(
                entries.map((entry) => ({ key: entry.key, data: ConcurrentUsageService.toRemoteData(entry) })),
                onlineOnly,
            )
            .pipe(
                map(() => true),
                tap(() => {
                    this.log.debug(`ConcurrentUsageService: uploadRemoteAll: SUCCESS writing ${keys.join(', ')}`);
                    this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemoteAll', {
                        state: 'success',
                        id,
                        keys,
                        durationMs: Math.round(performance.now() - start),
                    });
                }),
                catchError((error) => {
                    this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemoteAll', {
                        state: 'error',
                        id,
                        keys,
                        error: safeStringify(error),
                        durationMs: Math.round(performance.now() - start),
                    });
                    const errorMessage = `ConcurrentUsageService: uploadRemoteAll: ERROR entries could not be written to user-store: ${keys.join(', ')}. Error ${safeStringify(error)}`;
                    this.log.error(errorMessage);
                    return throwError(() => new Error(errorMessage));
                }),
            );
    }

    /**
     * Runs every upload in `uploads` to its end, no matter what the others do, and fails afterwards
     * if any of them failed.
     *
     * The straightforward `combineLatest([...])` cannot do that: the first failing upload errors the
     * whole batch, which unsubscribes every sibling still in flight. One key the backend rejects
     * would then keep every other entry from ever being uploaded, and a cancelled
     * `deleteRemoteAndPurge` could lose its purge after the delete had already landed. Each upload
     * therefore catches its own error, and the batch reports the collected failures only once all
     * of them have finished — the caller still learns that something is unsent (which
     * `DeviceLockManagementModalComponent.releaseLock` relies on: it must not release the lock while
     * data is still owed to the backend), but only after every other entry got its chance.
     *
     * @param uploads the uploads to run, each named for the error message and the log
     * @param operation the calling method, for the error message and the log
     * @returns an observable failing with the names of the failed uploads, or emitting `true`
     */
    private uploadIndependently(
        uploads: Array<{ name: string; upload$: Observable<unknown> }>,
        operation: string,
    ): Observable<unknown> {
        if (uploads.length === 0) {
            return of(true);
        }

        return forkJoin(
            uploads.map(({ name, upload$ }) =>
                upload$.pipe(
                    map(() => undefined),
                    catchError((error: unknown) => {
                        this.log.error(
                            `ConcurrentUsageService: ${operation}: "${name}" failed, the other entries are still uploaded: ${safeStringify(error)}`,
                        );
                        return of({ name, reason: error instanceof Error ? error.message : safeStringify(error) });
                    }),
                ),
            ),
        ).pipe(
            map((results) => results.filter(isDefined)),
            switchMap((failed) =>
                failed.length === 0
                    ? of(true)
                    : throwError(
                          () =>
                              new Error(
                                  `ConcurrentUsageService: ${operation}: ERROR ${failed.length} of ${uploads.length} uploads failed: ${failed
                                      .map(({ name, reason }) => `${name} (${reason})`)
                                      .join('; ')}`,
                              ),
                      ),
            ),
        );
    }

    /**
     * Reads concurrency data explicitly from remote store.
     *
     * Please note, this will not read from local storage, use `readDataForKey` for general usage.
     *
     * @param key The key to read.
     * @returns Observable<ConcurrentStorageData[]> An observable that emits the concurrency data read from remote store.
     */
    public readRemote(key: string): Observable<ConcurrentStorageData[]> {
        this.log.debug(`ConcurrentUsageService: readRemote: loading data for key "${key}"`);
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::readRemote', { id, key, state: 'attempt' });
        const start = performance.now(); // high-resolution timestamp

        return this.userStoreApi.getEntry(key).pipe(
            map((entry) => {
                this.log.debug(`ConcurrentUsageService: readDataForKey: SUCCESS loading data for key "${key}"`);
                if (!entry || !ConcurrentUsageService.isConcurrentUsageRemoteData(entry.data)) {
                    return [];
                }
                return [ConcurrentUsageService.toConcurrentStorageData(key, entry.data)];
            }),
            tap((response) => {
                this.paceInsights.trackEvent('ConcurrentUsageService::readDataForKey', {
                    state: 'success',
                    id,
                    key,
                    response: safeStringify(response),
                    durationMs: Math.round(performance.now() - start),
                });
            }),
            catchError((error) => {
                this.paceInsights.trackEvent('ConcurrentUsageService::readDataForKey', {
                    state: 'error',
                    id,
                    key,
                    error: safeStringify(error),
                    durationMs: Math.round(performance.now() - start),
                });
                const errorMessage = `ConcurrentUsageService: readDataForKey: ERROR data with following key could not be read from user-store: ${key}. Error ${safeStringify(error)}`;
                this.log.error(errorMessage);
                return throwError(() => new Error(errorMessage));
            }),
        );
    }

    /**
     * Writes concurrency data explicitly to remote store.
     *
     * Please note, this will not write into local storage, use `writeDataForKey` for general usage.
     *
     * @param concurrencyData The concurrency data to write.
     * @param onlineOnly Bypass the offline request queue and write straight to the backend, so the
     *     returned observable reports the real outcome — see `uploadAll` for when that is wanted.
     * @returns Observable<unknown> An observable that completes when the write operation is done.
     */
    public writeRemote(concurrencyData: ConcurrentStorageData, onlineOnly = false): Observable<unknown> {
        const remoteData = ConcurrentUsageService.toRemoteData(concurrencyData);
        this.log.debug(
            `ConcurrentUsageService: uploadRemote: writing data for key "${concurrencyData.key}" at timestamp "${concurrencyData.timestamp}"`,
        );
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemote', {
            state: 'attempt',
            id,
            key: concurrencyData.key,
            value: concurrencyData.value,
        });
        const start = performance.now(); // high-resolution timestamp

        return this.userStoreApi.putEntry(concurrencyData.key, remoteData, onlineOnly).pipe(
            tap(() => {
                this.log.debug(
                    `ConcurrentUsageService: uploadRemote: SUCCESS writing data for key "${concurrencyData.key}" at timestamp "${concurrencyData.timestamp}"`,
                );
                this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemote', {
                    state: 'success',
                    id,
                    key: concurrencyData.key,
                    durationMs: Math.round(performance.now() - start),
                });
            }),
            catchError((error) => {
                this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemote', {
                    state: 'error',
                    id,
                    key: concurrencyData.key,
                    error: safeStringify(error),
                    durationMs: Math.round(performance.now() - start),
                });
                const errorMessage = `ConcurrentUsageService: uploadRemote: ERROR data with following key could not be written to user-store: ${concurrencyData.key}. Error ${safeStringify(error)}`;
                this.log.error(errorMessage);
                return throwError(() => new Error(errorMessage));
            }),
        );
    }

    /**
     * Deletes concurrency data explicitly from remote store.
     *
     * Please note, this will not delete from local storage, use `deleteDataForKey` for general usage.
     *
     * @param key The key to delete.
     * @returns Observable<unknown> An observable that completes when the delete operation is done.
     */
    public deleteRemote(key: string): Observable<unknown> {
        this.log.debug(`ConcurrentUsageService: deleteRemote: removing data for key "${key}"`);
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::deleteRemote', { id, key, state: 'attempt' });
        const start = performance.now(); // high-resolution timestamp

        return this.userStoreApi.deleteEntry(key).pipe(
            tap(() => {
                this.log.debug(`ConcurrentUsageService: deleteRemote: SUCCESS removing data for key "${key}"`);
                this.paceInsights.trackEvent('ConcurrentUsageService::deleteRemote', {
                    state: 'success',
                    id,
                    key,
                    durationMs: Math.round(performance.now() - start),
                });
            }),
            catchError((error) => {
                this.paceInsights.trackEvent('ConcurrentUsageService::deleteRemote', {
                    state: 'error',
                    id,
                    key,
                    error: safeStringify(error),
                    durationMs: Math.round(performance.now() - start),
                });
                const errorMessage = `ConcurrentUsageService: deleteRemote: ERROR data with following key could not be deleted from user-store: ${key}. Error ${safeStringify(error)}`;
                this.log.error(errorMessage);
                return throwError(() => new Error(errorMessage));
            }),
        );
    }

    /**
     * Bulk-reads the entries of `keys` from the user's `AzUserStore`, unwrapping any entry shaped
     * like this service's own `{ value, timestamp }` wire format (see `writeRemote`/
     * `isConcurrentUsageRemoteData`) back down to its raw stored value — a defensive `JSON.parse`
     * that falls back to the string itself if the stored value was never JSON (e.g. a plain device
     * id). An entry NOT in that shape (written outside this service, e.g. directly via
     * `UserStoreApiService`) passes through unchanged. A key without an entry is missing from the
     * result.
     *
     * `UserStoreEffects.loadUserStore$` uses this to hydrate the user-store mirror with just its
     * own `USER_STORE_SYNCED_KEYS`, in one round trip — never the device-handover entries that
     * share the same namespace.
     *
     * @param keys the keys to read
     * @returns the entries that exist among `keys`, unwrapped
     */
    public getUserStoreEntries(keys: readonly string[]): Observable<UserStoreEntry[]> {
        return this.userStoreApi
            .getEntries(keys)
            .pipe(
                map((entries) =>
                    entries.map((entry) =>
                        ConcurrentUsageService.isConcurrentUsageRemoteData(entry.data)
                            ? { ...entry, data: ConcurrentUsageService.unwrapRemoteValue(entry.data.value) }
                            : entry,
                    ),
                ),
            );
    }

    private static unwrapRemoteValue(value: string): unknown {
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    }

    private static isConcurrentUsageRemoteData(data: unknown): data is ConcurrentUsageRemoteData {
        return (
            typeof data === 'object' &&
            data !== null &&
            typeof (data as ConcurrentUsageRemoteData).value === 'string' &&
            typeof (data as ConcurrentUsageRemoteData).timestamp === 'number'
        );
    }

    private static toRemoteData(data: ConcurrentStorageData): ConcurrentUsageRemoteData {
        return { value: data.value, timestamp: data.timestamp };
    }

    private static toConcurrentStorageData(key: string, data: ConcurrentUsageRemoteData): ConcurrentStorageData {
        return { key, value: data.value, timestamp: data.timestamp };
    }
}
